import { formatNumber } from "../../../../../infrastructure/utils/formatters";

export const METRIC_TYPE = {
  Event: 1,
  CurrentSnapshot: 2,
};

export const COMPARISON_STATE = {
  Comparable: 1,
  NoBaseline: 2,
  NoComparison: 3,
};

export const DEFAULT_FILTER_DAYS = 30;

/** Defer dashboard API calls until shell + filter options have mounted. */
export const DASHBOARD_BOOTSTRAP_DELAY_MS = 120;
export const DASHBOARD_API_RETRIES = 2;
export const DASHBOARD_API_RETRY_DELAY_MS = 650;

export const delay = (ms) =>
  new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });

export const isApiSuccess = (result) =>
  Boolean(result?.isSuccess ?? result?.IsSuccess);

export const getApiErrorMessage = (result, fallback) => {
  const message = result?.message ?? result?.Message;
  if (message != null && String(message).trim() !== "") {
    return String(message).trim();
  }
  return fallback;
};

/** GET with automatic retry — avoids flaky first load after navigation. */
export const fetchDashboardGet = async (
  requestFn,
  {
    retries = DASHBOARD_API_RETRIES,
    retryDelayMs = DASHBOARD_API_RETRY_DELAY_MS,
  } = {}
) => {
  let lastResult = null;
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      lastResult = await requestFn();
      if (isApiSuccess(lastResult)) return lastResult;
      if (lastResult == null) {
        lastResult = { isSuccess: false, message: "پاسخی از سرور دریافت نشد." };
      }
    } catch (error) {
      if (error?.code === "ERR_CANCELED") {
        lastResult = { isSuccess: false, message: "درخواست لغو شد." };
      } else {
        lastResult = {
          isSuccess: false,
          message: error?.message || "خطا در ارتباط با سرور",
        };
      }
    }
    if (attempt < retries) {
      await delay(retryDelayMs * (attempt + 1));
    }
  }
  return lastResult;
};

export const pick = (obj, ...keys) => {
  if (!obj) return undefined;
  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null) return obj[key];
  }
  return undefined;
};

export const createDefaultFilters = () => {
  const today = new Date();
  const from = new Date(today);
  from.setDate(from.getDate() - (DEFAULT_FILTER_DAYS - 1));

  return {
    dateFrom: toInputDate(from),
    dateTo: toInputDate(today),
    provinceId: "",
    cityId: "",
    projectType: "",
    minimumCapacityKw: "",
    maximumCapacityKw: "",
    needsLoan: "",
    lendingPartnerCode: "",
    contractorInfoId: "",
    requestStatus: "",
    registrationChannel: "",
  };
};

export const toInputDate = (date) => {
  if (!date) return "";
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export const parseSearchParamsToFilters = (searchParams) => {
  const defaults = createDefaultFilters();
  const read = (key) => searchParams.get(key) || defaults[key] || "";

  return {
    dateFrom: read("dateFrom") || defaults.dateFrom,
    dateTo: read("dateTo") || defaults.dateTo,
    provinceId: read("provinceId"),
    cityId: read("cityId"),
    projectType: read("projectType"),
    minimumCapacityKw: read("minimumCapacityKw"),
    maximumCapacityKw: read("maximumCapacityKw"),
    needsLoan: read("needsLoan"),
    lendingPartnerCode: read("lendingPartnerCode"),
    contractorInfoId: read("contractorInfoId"),
    requestStatus: read("requestStatus"),
    registrationChannel: read("registrationChannel"),
  };
};

export const filtersToSearchParams = (filters) => {
  const params = new URLSearchParams();
  const entries = [
    ["dateFrom", filters.dateFrom],
    ["dateTo", filters.dateTo],
    ["provinceId", filters.provinceId],
    ["cityId", filters.cityId],
    ["projectType", filters.projectType],
    ["minimumCapacityKw", filters.minimumCapacityKw],
    ["maximumCapacityKw", filters.maximumCapacityKw],
    ["needsLoan", filters.needsLoan],
    ["lendingPartnerCode", filters.lendingPartnerCode],
    ["contractorInfoId", filters.contractorInfoId],
    ["requestStatus", filters.requestStatus],
    ["registrationChannel", filters.registrationChannel],
  ];

  entries.forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      params.set(key, String(value).trim());
    }
  });

  return params;
};

export const filtersToApiQuery = (filters) => {
  const query = {};

  if (filters.dateFrom) query.dateFrom = filters.dateFrom;
  if (filters.dateTo) query.dateTo = filters.dateTo;
  if (filters.provinceId) query.provinceId = Number(filters.provinceId);
  if (filters.cityId) query.cityId = Number(filters.cityId);
  if (filters.projectType !== "") query.projectType = Number(filters.projectType);
  if (filters.minimumCapacityKw !== "")
    query.minimumCapacityKw = Number(filters.minimumCapacityKw);
  if (filters.maximumCapacityKw !== "")
    query.maximumCapacityKw = Number(filters.maximumCapacityKw);
  if (filters.needsLoan === "1") query.needsLoan = true;
  if (filters.needsLoan === "0") query.needsLoan = false;
  if (filters.lendingPartnerCode)
    query.lendingPartnerCode = filters.lendingPartnerCode;
  if (filters.contractorInfoId)
    query.contractorInfoId = Number(filters.contractorInfoId);
  if (filters.requestStatus !== "" && filters.requestStatus != null) {
    const raw = String(filters.requestStatus).trim();
    // Numeric values (management) or enum names (operator: InProcess, NotChecked, …)
    query.requestStatus = /^-?\d+$/.test(raw) ? Number(raw) : raw;
  }
  if (filters.registrationChannel !== "")
    query.registrationChannel = Number(filters.registrationChannel);

  return query;
};

export const formatCapacityValue = (value, unit = "kW") => {
  if (value === null || value === undefined) return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return "—";
  if (unit?.toLowerCase() === "mw" || (unit === "kW" && num >= 1000)) {
    return `${formatNumber(num / 1000, 2)} MW`;
  }
  return `${formatNumber(num, num % 1 === 0 ? 0 : 2)} kW`;
};

export const formatKpiValue = (kpi, currencyUnit = "IRR") => {
  if (kpi?.isAvailable === false || kpi?.IsAvailable === false) {
    return null;
  }

  const preformatted = pick(kpi, "formattedValue", "FormattedValue");
  if (preformatted != null && String(preformatted).trim() !== "") {
    return preformatted;
  }

  const value = pick(kpi, "value", "Value");
  const unit = pick(kpi, "unit", "Unit") || "";
  const currency = pick(kpi, "currencyUnit", "CurrencyUnit") || currencyUnit;

  if (value === null || value === undefined) return "—";

  if (unit === "ریال" || currency === "IRR") {
    return `${formatNumber(value)} ریال`;
  }

  if (unit === "kW" || unit?.toLowerCase() === "kw") {
    return formatCapacityValue(value, unit);
  }

  return `${formatNumber(value)}${unit ? ` ${unit}` : ""}`;
};

export const formatComparisonLine = (kpi) => {
  const metricType = pick(kpi, "metricType", "MetricType");
  if (metricType === METRIC_TYPE.CurrentSnapshot) {
    return "نمای لحظه‌ای";
  }

  const pct = pick(kpi, "percentageChange", "PercentageChange");
  const abs = pick(kpi, "absoluteChange", "AbsoluteChange");
  const state = pick(kpi, "comparisonState", "ComparisonState");

  if (state === COMPARISON_STATE.NoBaseline) {
    return abs > 0 ? "بدون مبنای دوره قبل" : "بدون داده دوره قبل";
  }

  if (pct === null || pct === undefined) {
    return abs === null || abs === undefined ? "—" : `تغییر: ${formatNumber(abs)}`;
  }

  const sign = pct > 0 ? "+" : "";
  return `${sign}${formatNumber(pct, 2)}٪ نسبت به دوره قبل`;
};

export const buildDrillDownHref = (drillDown) => {
  const path = pick(drillDown, "path", "Path");
  const query = pick(drillDown, "query", "Query") || {};
  if (!path) return null;

  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      params.set(key, String(value));
    }
  });

  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
};

export const normalizeFilterOptions = (data) => ({
  provinces: pick(data, "provinces", "Provinces") || [],
  cities: pick(data, "cities", "Cities") || [],
  projectTypes: pick(data, "projectTypes", "ProjectTypes") || [],
  requestStatuses: pick(data, "requestStatuses", "RequestStatuses") || [],
  lendingPartners: pick(data, "lendingPartners", "LendingPartners") || [],
  contractors: pick(data, "contractors", "Contractors") || [],
  registrationChannels:
    pick(data, "registrationChannels", "RegistrationChannels") || [],
});

export const normalizeExecutiveKpis = (data) => ({
  currencyUnit: pick(data, "currencyUnit", "CurrencyUnit") || "IRR",
  appliedFilter: pick(data, "appliedFilter", "AppliedFilter"),
  kpis: pick(data, "kpis", "Kpis") || [],
});
